import type { ChannelClient } from '@hibro/platform-core';
import type {
  IConfigurationService,
  ILauncherService,
  IModuleService,
  INlsService,
  IProductConfigService,
  IThemeService,
  IUpdateService,
  IWhenContextService,
  ResolvedContributions,
  ResolvedView,
  WindowControlsApi,
} from '@hibro/shared';
import { CONTRIBUTIONS_CHANGED_EVENT } from '@hibro/shared';
import { cn, Toaster, UiClientConfigProvider } from '@hibro/ui-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { OpenExternalBridge } from '../app-links/open';
import {
  CustomHeader,
  type WindowControlsBridge,
} from '../components/custom-header/custom-header';
import {
  ViewEventBusContext,
  type ViewEventSubscribe,
} from '../event-bus/event-bus';
import { useT } from '../i18n/i18n';
import {
  NavigationContext,
  type NavigationSource,
} from '../navigation/navigation';
import { SettingsWindow } from '../panels/settings-window';
import { isSettingsWindowSurface } from '../panels/settings-window/ids';
import { type LoadUi, UiRegistry } from '../runtime/ui-registry';
import { resolveOverlayPortalContainer } from '../theme/portal-container';
import type { DetachBridge } from './detach';
import type { ExternalBridge } from './external-surface';
import type { IframeBridge } from './iframe-surface';
import { OverlayProvider } from './overlay-host';
import { usePageViewLoading } from './page-view-loading';
import { FragmentSurface, SurfaceView } from './surface-host';
import { SurfaceHostedInProvider } from './surface-hosted-in';
import { SurfaceLoadingIndicator } from './surface-loading';

export interface DetachedSurfaceProps {
  /** The view popped out into this window (from `parseSurfaceTarget`). */
  viewId: string;
  moduleId: string;
  /**
   * The detached fragment id (架构方案 §5.8 通用 Detachable). When present this
   * window renders only that fragment via `registerDetachable`; absent = the
   * whole view is detached (backward-compatible special case).
   */
  fragmentId?: string;
  /** Instance discriminator for a per-instance fragment window (e.g. `c1`). */
  instanceKey?: string;
  /** Opaque, module-defined identity forwarded to the fragment render source. */
  payload?: unknown;
  /**
   * Whether to render the standard detached-window chrome (CustomHeader +
   * attach-back). `false` = bare full-bleed mount; the view/fragment paints its
   * own drag region and window controls via `windowControlsBridge`.
   */
  chrome?: boolean;
  /** Auto RPC proxy for the host module service (shared instance, §4.3). */
  moduleService: IModuleService;
  /** User-settings RPC for the preferences window Notifications page. */
  configService?: IConfigurationService;
  /** Locale preference for the preferences window Language page. */
  nlsService?: INlsService;
  /** Color scheme for the preferences window Appearance page. */
  themeService?: IThemeService;
  /** Lazy UI importers keyed by module id, discovered in the app shell. */
  uiLoaders: Map<string, LoadUi>;
  /**
   * Channel client for EventBus subscriptions (见 12): when provided, the
   * surface re-resolves its view on `CONTRIBUTIONS_CHANGED_EVENT` so a
   * hot-installed/updated module reflects without a restart. Optional to keep
   * existing callers working.
   */
  client?: ChannelClient;
  /**
   * Bridge for in-DOM `iframe` surfaces (见 9). Lets an `iframe` view popped
   * into this window mount its sandboxed `<iframe>` here (per-window), so detach
   * supports iframe modules too.
   */
  iframeBridge?: IframeBridge;
  /**
   * Bridge to the external-process host (§3.1/§5.8); injected by the shell so a
   * popped-out view keeps its `ViewProps.external` control over this module's
   * processes (a self-painted panel works detached exactly as inline).
   */
  externalBridge?: ExternalBridge;
  /**
   * Source of `navigate(view:<id>, …)` locations (见 8); injected by the
   * shell. The detached window receives the same `hibro:view:navigate` fan-out as
   * the main window, so a navigation to a popped-out view positions it here.
   */
  navigation?: NavigationSource;
  /**
   * Custom title-bar window controls bridge (§5.8); injected by the shell. Gives
   * the detached window the same frameless chrome (minimize/maximize/close +
   * macOS traffic-light spacing) as the main workbench window.
   */
  windowControlsBridge?: WindowControlsBridge;
  /**
   * Detach mechanism bridge (§5.8 通用 Detachable); injected by the shell so a
   * fragment rendered in this window gets `detach`/`detached` via its props too.
   */
  detachBridge?: DetachBridge;
  /**
   * Old platform settings body for this window's developer-options page.
   * Only the preferences window reads it.
   */
  developerSettings?: ReactNode;
  /** About page: running build and the manual version check. */
  updateService?: IUpdateService;
  /** About page: system browser when no sync open bridge is present. */
  launcherService?: ILauncherService;
  /** About page: `app.homepage`, `app.termsUrl`, `app.privacyUrl`. */
  productConfigService?: IProductConfigService;
  /** About page: web open that must stay inside the click. */
  openExternalBridge?: OpenExternalBridge;
  /** Slash-command page: module `when` keys, same snapshot as search. */
  whenContextService?: IWhenContextService;
}

/**
 * Single-surface window content (架构方案 §5.8 `detach`). Renders only the one
 * popped-out view, filling the window, instead of the full `Workbench`. It
 * resolves the real `ResolvedView` (kind/url) from `listContributions`
 * and renders it through the `SurfaceView` dispatcher, so an `inline` view shows
 * inline-dom and an `iframe` view mounts its sandboxed `<iframe>` here (见 9)
 * — reusing the same RPC proxies/UI loaders as the main window (§4.3).
 */
export function DetachedSurface({
  viewId,
  fragmentId,
  instanceKey,
  payload,
  chrome,
  moduleService,
  configService,
  nlsService,
  themeService,
  uiLoaders,
  iframeBridge,
  externalBridge,
  client,
  navigation,
  windowControlsBridge,
  detachBridge,
  developerSettings,
  updateService,
  launcherService,
  productConfigService,
  openExternalBridge,
  whenContextService,
}: DetachedSurfaceProps): ReactNode {
  const t = useT();
  const bare = chrome === false;
  // Keep the full contributed view set (not just the popped-out view) so the
  // per-window OverlayProvider can resolve any view an overlay hosts (架构方案
  // §5.8); the primary surface is derived from it by id.
  const [views, setViews] = useState<ResolvedView[]>([]);
  const [viewsReady, setViewsReady] = useState(false);
  const [runtimeUiEntries, setRuntimeUiEntries] = useState<
    ResolvedContributions['runtimeUiEntries']
  >([]);

  const refresh = useCallback(() => {
    let cancelled = false;
    void moduleService
      .listContributions()
      .then((contributions) => {
        if (!cancelled) {
          setViews(contributions.views);
          setRuntimeUiEntries(contributions.runtimeUiEntries);
          setViewsReady(true);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          logViewCatalogFailure(err);
          setViewsReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [moduleService]);

  useEffect(() => refresh(), [refresh]);

  // Re-resolve when the active module set changes at runtime (见 12).
  useEffect(() => {
    if (!client) {
      return;
    }
    const dispose = client.listen(CONTRIBUTIONS_CHANGED_EVENT, () => {
      refresh();
    });
    return dispose;
  }, [client, refresh]);

  const view = useMemo(
    () => views.find((v) => v.id === viewId) ?? null,
    [views, viewId],
  );

  // Registry over the full view set so both the popped-out view and any
  // overlay-hosted view activate/register normally (lazy — only rendered ones
  // activate). Inline-dom views consume it.
  const registry = useMemo(
    () => new UiRegistry(uiLoaders, views, undefined, runtimeUiEntries),
    [uiLoaders, views, runtimeUiEntries],
  );

  // General EventBus subscription for the popped-out view (架构方案 §5.15), bound
  // to this window's channel. `client` is optional here, so fall back to a no-op
  // subscriber (the view degrades gracefully rather than throwing).
  const onEvent = useMemo<ViewEventSubscribe>(
    () =>
      client
        ? (event, handler) => client.listen(event, handler)
        : () => () => undefined,
    [client],
  );

  // Bare chrome: hand OS window controls to the fragment so it can self-draw
  // minimize/maximize/close. `WindowControlsBridge` is a structural superset of
  // `WindowControlsApi` (its extra `chrome`/`channel` fields go unused here).
  const fragmentWindowControls: WindowControlsApi | undefined = bare
    ? windowControlsBridge
    : undefined;

  const settingsWindow = isSettingsWindowSurface(viewId, fragmentId);

  return (
    <SurfaceHostedInProvider value="detached">
      <UiClientConfigProvider portalContainer={resolveOverlayPortalContainer()}>
        <ViewEventBusContext.Provider value={onEvent}>
          <NavigationContext.Provider value={navigation}>
            {/* Per-window OverlaySurface host (架构方案 §5.8): a popped-out view
              opens overlays in-process, so they render in THIS window. No respond
              channel (overlayService) is wired here on purpose — service-side
              (`context.overlay`) overlays stay in the main window, not duplicated. */}
            <OverlayProvider
              client={client}
              views={views}
              registry={registry}
              moduleService={moduleService}
              presentWindow={
                windowControlsBridge
                  ? () => windowControlsBridge.present()
                  : undefined
              }
            >
              <div
                className={cn(
                  'flex h-full w-full flex-col overflow-hidden font-[system-ui,sans-serif] text-foreground',
                  !bare && 'bg-card',
                )}
              >
                {/* Same shared title bar as the main workbench (§5.8): drag region
            + window controls. No attach-back button — closing the window *is*
            attaching back (the host drops the handle and the workbench re-inlines
            the view), so a second control for it was one button too many. The
            workbench-side placeholder still offers it explicitly. Bare
            (`chrome: false`) skips this — the view paints its own chrome. */}
                {!bare ? <CustomHeader bridge={windowControlsBridge} /> : null}
                {settingsWindow ? (
                  <div className="flex min-h-0 flex-1">
                    <SettingsWindow
                      moduleService={moduleService}
                      windowControls={windowControlsBridge}
                      configService={configService}
                      client={client}
                      nlsService={nlsService}
                      themeService={themeService}
                      developerSettings={developerSettings}
                      updateService={updateService}
                      launcherService={launcherService}
                      productConfigService={productConfigService}
                      openExternalBridge={openExternalBridge}
                      whenContextService={whenContextService}
                      renderModuleView={(moduleId, sectionViewId) => {
                        if (!viewsReady) {
                          return (
                            <PendingSettingsSectionView
                              viewId={sectionViewId}
                            />
                          );
                        }
                        const hosted = views.find(
                          (item) => item.id === sectionViewId,
                        );
                        if (!hosted || hosted.moduleId !== moduleId) {
                          return null;
                        }
                        return (
                          <SurfaceView
                            viewId={hosted.id}
                            moduleId={hosted.moduleId}
                            registry={registry}
                            moduleService={moduleService}
                            kind={hosted.kind}
                            url={hosted.url}
                            autoLaunch={hosted.autoLaunch}
                            externalEntry={hosted.externalEntry}
                            iframeBridge={iframeBridge}
                            externalBridge={externalBridge}
                            detachBridge={detachBridge}
                            subscribeContributionsChanged={
                              client
                                ? (listener) =>
                                    client.listen(
                                      CONTRIBUTIONS_CHANGED_EVENT,
                                      listener,
                                    )
                                : undefined
                            }
                            concealLoadError
                          />
                        );
                      }}
                    />
                  </div>
                ) : view ? (
                  <div className={cn('flex min-h-0 flex-1')}>
                    {fragmentId ? (
                      <FragmentSurface
                        viewId={view.id}
                        moduleId={view.moduleId}
                        fragmentId={fragmentId}
                        instanceKey={instanceKey}
                        payload={payload}
                        registry={registry}
                        moduleService={moduleService}
                        detachBridge={detachBridge}
                        windowControls={fragmentWindowControls}
                      />
                    ) : (
                      <SurfaceView
                        viewId={view.id}
                        moduleId={view.moduleId}
                        // A whole-view detach can still target one instance tab
                        // (§5.7 视图多实例): the surface target carries the key and
                        // the view reads it as `ViewProps.instanceKey` — the
                        // built-in browser resolves its editor slot from exactly
                        // that (见 22).
                        instanceKey={instanceKey}
                        registry={registry}
                        moduleService={moduleService}
                        kind={view.kind}
                        url={view.url}
                        iframeBridge={iframeBridge}
                        externalBridge={externalBridge}
                        detachBridge={detachBridge}
                        subscribeContributionsChanged={
                          client
                            ? (listener) =>
                                client.listen(
                                  CONTRIBUTIONS_CHANGED_EVENT,
                                  listener,
                                )
                            : undefined
                        }
                      />
                    )}
                  </div>
                ) : (
                  <SurfaceLoadingIndicator label={t('surface.view.loading')} />
                )}
              </div>
              <Toaster />
            </OverlayProvider>
          </NavigationContext.Provider>
        </ViewEventBusContext.Provider>
      </UiClientConfigProvider>
    </SurfaceHostedInProvider>
  );
}

/** The view catalog failed. Sections then read as unwired, so the cause stays in the log. */
function logViewCatalogFailure(err: unknown): void {
  const reason = err instanceof Error ? err.message : String(err);
  (
    globalThis as {
      hibroLog?: {
        append: (entry: {
          level: string;
          kind: string;
          message: string;
          error?: unknown;
        }) => void;
      };
    }
  ).hibroLog?.append({
    level: 'error',
    kind: 'log',
    message: 'settings window view catalog load failed',
    error: { reason },
  });
}

/**
 * Holds the preferences page spinner until the view catalog arrives.
 * An empty catalog is the state before that read, not a missing view.
 */
function PendingSettingsSectionView({ viewId }: { viewId: string }): ReactNode {
  usePageViewLoading(viewId, true);
  return null;
}
